import { jsx, jsxs } from "react/jsx-runtime";
import React, { useMemo, useState, useRef, useEffect, useCallback } from "react";
import classNames from "classnames";
import { HTMLTable, Icon, Classes, InputGroup, Button, Intent, Spinner, Tooltip, Menu, MenuItem } from "@blueprintjs/core";
import axios from "axios";
import { useDebounce } from "use-debounce";
import { getFileIcon, IconWithGitOverlay, SEQUENCE_EXTENSIONS, ALL_EDITABLE_EXTENSIONS } from "./utils/fileIcons";
import { showFileContextMenu } from "./utils/fileContextMenu";
import { matchesQuery } from "./utils/searchUtils";
import { getErrorMessage } from "./utils/errorUtils";
import { getFileClipboard, subscribeToFileClipboard, setRenamingState, setActiveTableSelection } from "./utils/fileClipboard";
const DEFAULT_WIDTHS = {
  name: 250,
  description: 300,
  inInventory: 110,
  modifiedDate: 180,
  lastAccessed: 180,
  path: 250
};
const BASE_PATTERNS = [
  "in_inventory:yes",
  "in_inventory:no",
  "in_inventory:true",
  "in_inventory:false",
  "inventory:yes",
  "inventory:no",
  "inventory:true",
  "inventory:false",
  "ininventory:yes",
  "ininventory:no",
  "ininventory:true",
  "ininventory:false",
  "folder:"
];
const getGhostSuggestion = (query, availableFolders = []) => {
  if (!query) return null;
  const match = query.match(/(?:^|\s)(\S+)$/);
  if (!match) return null;
  const lastToken = match[1];
  if (lastToken.length < 2) return null;
  const lowerToken = lastToken.toLowerCase();
  const folderPatterns = (availableFolders || []).map((f) => {
    const name = typeof f === "string" ? f : f.name || f.path || "";
    return name ? name : null;
  }).filter((name) => name && name.toLowerCase() !== "projects").map((name) => `folder:${name}`);
  const allPatterns = lowerToken === "folder:" ? BASE_PATTERNS : [...BASE_PATTERNS, ...folderPatterns];
  for (const pattern of allPatterns) {
    if (pattern.toLowerCase().startsWith(lowerToken)) {
      const suffix = pattern.slice(lastToken.length);
      if (suffix) {
        return { lastToken, suffix, pattern };
      }
    }
  }
  return null;
};
const ProjectFilesTableView = React.forwardRef(({ folderPath, availableFiles, availableFolders, onNavigate, onPreview, activePreviewPath, isRecentFiles, gitStatus = null, customItems = null }, ref) => {
  const [urlSearchQuery, setUrlSearchQuery] = useState(() => {
    if (typeof window !== "undefined" && window.location.hash.includes("?")) {
      const params = new URLSearchParams(window.location.hash.split("?")[1]);
      return params.get("search") || params.get("searchQuery") || "";
    }
    return "";
  });
  const [filterQuery, setFilterQuery] = useState("");
  useEffect(() => {
    const syncSearchFromUrl = () => {
      if (typeof window !== "undefined" && window.location.hash.includes("?")) {
        const params = new URLSearchParams(window.location.hash.split("?")[1]);
        const searchParam = params.get("search") || params.get("searchQuery");
        if (searchParam !== null) {
          setUrlSearchQuery(searchParam);
          setFilterQuery("");
        } else {
          setUrlSearchQuery("");
        }
      } else {
        setUrlSearchQuery("");
      }
    };
    syncSearchFromUrl();
    window.addEventListener("hashchange", syncSearchFromUrl);
    return () => window.removeEventListener("hashchange", syncSearchFromUrl);
  }, []);
  const [debouncedUrlSearchQuery] = useDebounce(urlSearchQuery, 300);
  const [debouncedFilterQuery] = useDebounce(filterQuery, 300);
  const [urlSequenceSearchResults, setUrlSequenceSearchResults] = useState([]);
  const [filterSequenceSearchResults, setFilterSequenceSearchResults] = useState([]);
  useEffect(() => {
    if (debouncedUrlSearchQuery && debouncedUrlSearchQuery.length >= 20 && /^[ATGCNRYKMSWBDHVatgcnrykmswbdhv]+$/.test(debouncedUrlSearchQuery)) {
      const searchSeq = async () => {
        try {
          const res = await axios.get(`/api/search/sequence?query=${debouncedUrlSearchQuery}`);
          setUrlSequenceSearchResults(res.data.results || []);
        } catch (e) {
          console.error("Sequence search failed:", e);
          setUrlSequenceSearchResults([]);
        }
      };
      searchSeq();
    } else {
      setUrlSequenceSearchResults([]);
    }
  }, [debouncedUrlSearchQuery]);
  useEffect(() => {
    if (debouncedFilterQuery && debouncedFilterQuery.length >= 20 && /^[ATGCNRYKMSWBDHVatgcnrykmswbdhv]+$/.test(debouncedFilterQuery)) {
      const searchSeq = async () => {
        try {
          const res = await axios.get(`/api/search/sequence?query=${debouncedFilterQuery}`);
          setFilterSequenceSearchResults(res.data.results || []);
        } catch (e) {
          console.error("Sequence search failed:", e);
          setFilterSequenceSearchResults([]);
        }
      };
      searchSeq();
    } else {
      setFilterSequenceSearchResults([]);
    }
  }, [debouncedFilterQuery]);
  const [suggestionDismissed, setSuggestionDismissed] = useState(false);
  const [lastToken, setLastToken] = useState("");
  const ghostSuggestion = useMemo(() => getGhostSuggestion(filterQuery, availableFolders), [filterQuery, availableFolders]);
  useEffect(() => {
    const currentToken = ghostSuggestion ? ghostSuggestion.lastToken : "";
    if (currentToken !== lastToken) {
      setSuggestionDismissed(false);
      setLastToken(currentToken);
    }
  }, [ghostSuggestion, lastToken]);
  const handleSearchKeyDown = (e) => {
    if (ghostSuggestion && !suggestionDismissed) {
      if (e.key === "Tab" || e.key === "Enter" || e.key === "ArrowRight") {
        e.preventDefault();
        e.stopPropagation();
        setFilterQuery((prev) => prev + ghostSuggestion.suffix);
      } else if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        setSuggestionDismissed(true);
      }
    }
  };
  const [sortColumn, setSortColumn] = useState(isRecentFiles ? "lastAccessed" : "name");
  const [sortDirection, setSortDirection] = useState(isRecentFiles ? "desc" : "asc");
  const [hoveredResizeCol, setHoveredResizeCol] = useState(null);
  const [activeResizeCol, setActiveResizeCol] = useState(null);
  const [columnWidths, setColumnWidths] = useState(() => {
    try {
      const saved = localStorage.getItem("ove_table_column_widths");
      return saved ? JSON.parse(saved) : DEFAULT_WIDTHS;
    } catch (e) {
      return DEFAULT_WIDTHS;
    }
  });
  const [selectedPaths, setSelectedPaths] = useState([]);
  const [lastClickedPath, setLastClickedPath] = useState(null);
  const [renamingPath, setRenamingPath] = useState(null);
  const [tempRenameValue, setTempRenameValue] = useState("");
  const [renamingSource, setRenamingSource] = useState(null);
  const resizingRef = useRef(null);
  const containerRef = useRef(null);
  useEffect(() => {
    const {
      renamingPath: initialRenaming,
      tempRenameValue: initialTemp,
      renamingSource: initialSource
    } = getFileClipboard();
    setRenamingPath(initialRenaming);
    setTempRenameValue(initialTemp);
    setRenamingSource(initialSource);
    return subscribeToFileClipboard((state) => {
      console.log("[DEBUG] TableView clipboard update:", state);
      setRenamingPath(state.renamingPath);
      setTempRenameValue(state.tempRenameValue);
      setRenamingSource(state.renamingSource);
    });
  }, []);
  useEffect(() => {
    setActiveTableSelection({ selectedPaths, lastClickedPath, folderPath });
  }, [selectedPaths, lastClickedPath, folderPath]);
  useEffect(() => {
    setSelectedPaths([]);
    setLastClickedPath(null);
  }, [folderPath]);
  useEffect(() => {
    localStorage.setItem("ove_table_column_widths", JSON.stringify(columnWidths));
  }, [columnWidths]);
  const handleResizeStart = (e, column) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveResizeCol(column);
    resizingRef.current = {
      column,
      startX: e.pageX,
      startWidth: columnWidths[column] || DEFAULT_WIDTHS[column]
    };
    document.addEventListener("mousemove", handleResizeMove);
    document.addEventListener("mouseup", handleResizeEnd);
  };
  const handleResizeMove = (e) => {
    if (!resizingRef.current) return;
    const { column, startX, startWidth } = resizingRef.current;
    const diff = e.pageX - startX;
    const newWidth = Math.max(50, startWidth + diff);
    setColumnWidths((prev) => ({
      ...prev,
      [column]: newWidth
    }));
  };
  const handleResizeEnd = () => {
    setActiveResizeCol(null);
    resizingRef.current = null;
    document.removeEventListener("mousemove", handleResizeMove);
    document.removeEventListener("mouseup", handleResizeEnd);
  };
  const currentItems = useMemo(() => {
    if (customItems) return customItems;
    if (isRecentFiles) {
      try {
        const historyStr = localStorage.getItem("ove_file_history") || "[]";
        const history = JSON.parse(historyStr);
        const allExtensions = [".gen.gb", ".gb", ".gbk", ".genbank", ".fasta", ".fa", ".assembly", ".sam.json", ".jbrowse.zip", ".ab1"];
        let validHistory = history.filter((h) => h.path && allExtensions.some((ext) => h.path.toLowerCase().endsWith(ext)));
        if (availableFiles !== null) {
          validHistory = validHistory.filter((h) => availableFiles.some((f) => f.path === h.path));
        }
        return validHistory.map((h) => {
          const file = availableFiles?.find((f) => f.path === h.path);
          return {
            ...file || { name: h.path.split("/").pop(), path: h.path },
            lastAccessed: h.lastAccessed,
            type: "file"
          };
        }).slice(0, 10);
      } catch (e) {
        console.error("Error reading history:", e);
        return [];
      }
    }
    const items = [];
    const isRoot = folderPath === "projects";
    if (availableFiles) {
      availableFiles.forEach((file) => {
        if (isRoot) {
          items.push({ ...file, type: "file" });
        } else if (file.path.startsWith(folderPath + "/")) {
          items.push({ ...file, type: "file" });
        }
      });
    }
    return items;
  }, [folderPath, availableFiles, isRecentFiles, customItems]);
  useEffect(() => {
    if (customItems) {
      console.log("[DEBUG] ProjectFilesTableView received customItems:", customItems);
    }
  }, [customItems]);
  const filteredAndSortedItems = useMemo(() => {
    let result = [...currentItems];
    if (urlSearchQuery) {
      result = result.filter(
        (item) => matchesQuery(item, urlSearchQuery) || urlSequenceSearchResults && urlSequenceSearchResults.includes(item.path)
      );
    }
    if (filterQuery) {
      result = result.filter(
        (item) => matchesQuery(item, filterQuery) || filterSequenceSearchResults && filterSequenceSearchResults.includes(item.path)
      );
    }
    result.sort((a, b) => {
      let valA, valB;
      if (sortColumn === "inInventory") {
        valA = a.inInventory !== void 0 ? Boolean(a.inInventory) : Boolean(a.in_inventory);
        valB = b.inInventory !== void 0 ? Boolean(b.inInventory) : Boolean(b.in_inventory);
      } else {
        valA = a[sortColumn];
        valB = b[sortColumn];
      }
      if (valA === valB) return 0;
      if (valA == null) return sortDirection === "asc" ? -1 : 1;
      if (valB == null) return sortDirection === "asc" ? 1 : -1;
      if (typeof valA === "string") valA = valA.toLowerCase();
      if (typeof valB === "string") valB = valB.toLowerCase();
      if (valA < valB) return sortDirection === "asc" ? -1 : 1;
      if (valA > valB) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
    return result;
  }, [currentItems, urlSearchQuery, filterQuery, urlSequenceSearchResults, filterSequenceSearchResults, sortColumn, sortDirection]);
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (e.key === "F2") {
        console.log("[DEBUG] TableView global F2. SelectedPaths:", selectedPaths);
        const activeEl = document.activeElement;
        const activeTag = activeEl.tagName.toLowerCase();
        console.log("[DEBUG] Active element:", activeTag, activeEl.className);
        if (activeTag === "input" || activeTag === "textarea") {
          console.log("[DEBUG] F2 ignored: active element is input/textarea");
          return;
        }
        const isTableFocused = containerRef.current?.contains(document.activeElement);
        console.log("[DEBUG] Is table focused?", isTableFocused);
        if (isTableFocused && selectedPaths.length === 1) {
          const item = filteredAndSortedItems.find((i) => i.path === selectedPaths[0]);
          console.log("[DEBUG] TableView F2 target item:", item);
          if (item) {
            e.preventDefault();
            e.stopPropagation();
            console.log("[DEBUG] Calling setRenamingState from TableView:", item.path);
            setRenamingState(item.path, item.name, "table");
          }
        }
      }
      if ((e.ctrlKey || e.metaKey) && (e.key?.toLowerCase() === "c" || e.code === "KeyC")) {
        const activeTag = document.activeElement?.tagName?.toLowerCase();
        const isEditable = document.activeElement?.isContentEditable || activeTag === "input" || activeTag === "textarea";
        const isTableFocused = containerRef.current?.contains(document.activeElement);
        console.log("[TableView Global Ctrl+C Pressed]", {
          key: e.key,
          code: e.code,
          activeTag,
          activeElement: document.activeElement,
          isEditable,
          isTableFocused,
          selectedPaths,
          itemsCount: filteredAndSortedItems?.length
        });
        if (isEditable) {
          console.warn("[TableView Global Ctrl+C] Ignored because active element is editable.");
          return;
        }
        if (isTableFocused && selectedPaths.length > 0) {
          const items = selectedPaths.map((path) => filteredAndSortedItems.find((i) => i.path === path)).filter(Boolean);
          console.log("[TableView Global Ctrl+C] Copying items:", items);
          if (items.length > 0) {
            e.preventDefault();
            e.stopPropagation();
            window.dispatchEvent(new CustomEvent("file-operation-copy", { detail: { items } }));
          }
        }
      }
      if (e.altKey && (e.key?.toLowerCase() === "k" || e.code === "KeyK")) {
        e.preventDefault();
        e.stopPropagation();
        const searchInput = document.getElementById("table-search-input") || document.querySelector('[data-testid="search-input"] input, [data-testid="search-input"]');
        if (searchInput) {
          searchInput.focus();
          searchInput.select && searchInput.select();
        }
      }
      if (e.altKey && (e.key?.toLowerCase() === "n" || e.code === "KeyN")) {
        e.preventDefault();
        e.stopPropagation();
        window.dispatchEvent(new CustomEvent("open-new-item-dialog"));
      }
    };
    window.addEventListener("keydown", handleGlobalKeyDown, true);
    return () => window.removeEventListener("keydown", handleGlobalKeyDown, true);
  }, [selectedPaths, filteredAndSortedItems]);
  const handleKeyDown = useCallback((e) => {
    if ((e.ctrlKey || e.metaKey) && (e.key?.toLowerCase() === "c" || e.code === "KeyC")) {
      const activeTag = document.activeElement?.tagName?.toLowerCase();
      const isEditable = document.activeElement?.isContentEditable || activeTag === "input" || activeTag === "textarea";
      console.log("[TableView Local Ctrl+C Pressed]", {
        key: e.key,
        code: e.code,
        activeTag,
        activeElement: document.activeElement,
        isEditable,
        selectedPaths
      });
      if (isEditable) return;
      if (selectedPaths.length > 0) {
        const items = selectedPaths.map((path) => filteredAndSortedItems.find((i) => i.path === path)).filter(Boolean);
        if (items.length > 0) {
          console.log("[TableView Local Ctrl+C] Dispatching file-operation-copy:", items);
          e.preventDefault();
          e.stopPropagation();
          window.dispatchEvent(new CustomEvent("file-operation-copy", { detail: { items } }));
        } else {
          console.warn("[TableView Local Ctrl+C] No matching items found for selectedPaths:", selectedPaths);
        }
      } else {
        console.warn("[TableView Local Ctrl+C] selectedPaths is empty.");
      }
    }
    if ((e.ctrlKey || e.metaKey) && (e.key?.toLowerCase() === "x" || e.code === "KeyX")) {
      const activeTag = document.activeElement?.tagName?.toLowerCase();
      const isEditable = document.activeElement?.isContentEditable || activeTag === "input" || activeTag === "textarea";
      if (isEditable) return;
      if (selectedPaths.length > 0) {
        const items = selectedPaths.map((path) => filteredAndSortedItems.find((i) => i.path === path)).filter(Boolean);
        if (items.length > 0) {
          e.preventDefault();
          e.stopPropagation();
          window.dispatchEvent(new CustomEvent("file-operation-copy", { detail: { items, cut: true } }));
        }
      }
    }
    if ((e.ctrlKey || e.metaKey) && (e.key?.toLowerCase() === "v" || e.code === "KeyV")) {
      const activeTag = document.activeElement?.tagName?.toLowerCase();
      const isEditable = document.activeElement?.isContentEditable || activeTag === "input" || activeTag === "textarea";
      if (isEditable) return;
      e.preventDefault();
      e.stopPropagation();
      window.dispatchEvent(new CustomEvent("file-operation-paste", { detail: { targetFolder: folderPath || "." } }));
    }
    if (e.key === "Delete" || e.metaKey && e.key === "Backspace") {
      const activeTag = document.activeElement.tagName.toLowerCase();
      if (activeTag === "input" || activeTag === "textarea") return;
      if (selectedPaths.length > 0) {
        const items = selectedPaths.map((path) => filteredAndSortedItems.find((i) => i.path === path)).filter(Boolean);
        if (items.length > 0) {
          e.preventDefault();
          e.stopPropagation();
          window.dispatchEvent(new CustomEvent("file-operation-delete", { detail: { items } }));
        }
      }
    }
  }, [selectedPaths, filteredAndSortedItems, folderPath]);
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection((prev) => prev === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };
  const renderSortIndicator = (column) => {
    if (sortColumn !== column) return null;
    return /* @__PURE__ */ jsx(Icon, { icon: sortDirection === "asc" ? "chevron-up" : "chevron-down" });
  };
  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp * 1e3);
    return date.toLocaleString();
  };
  const handleDragStart = (e, item) => {
    e.dataTransfer.setData("itemPaths", JSON.stringify([item.path]));
    e.dataTransfer.effectAllowed = "move";
  };
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const handleDragOver = (e) => {
    if (!Array.from(e.dataTransfer.types).includes("Files")) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
    setIsDragOver(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };
  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      setIsUploading(true);
      const formData = new FormData();
      Array.from(e.dataTransfer.files).forEach((file) => {
        formData.append("file", file);
      });
      const targetPath = folderPath === "projects" || !folderPath ? "." : folderPath;
      formData.append("targetPath", targetPath);
      try {
        const response = await axios.post("/api/file/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        if (response.data.success) {
          window.dispatchEvent(new CustomEvent("file-saved", {
            detail: {
              path: response.data.saved_paths?.[0] || targetPath,
              last_modified: Date.now() / 1e3
            }
          }));
          if (response.data.saved_paths && response.data.saved_paths.length > 0) {
            onNavigate(response.data.saved_paths[0]);
          }
        }
      } catch (error) {
        console.error("Error uploading files:", error);
        window.toastr?.error(`Error uploading files: ${getErrorMessage(error)}`);
      } finally {
        setIsUploading(false);
      }
    }
  };
  const getColumnStyle = (col) => ({
    width: columnWidths[col] || DEFAULT_WIDTHS[col],
    cursor: "pointer",
    position: "sticky",
    top: 0,
    backgroundColor: "white",
    zIndex: 20
  });
  const renderResizeHandle = (col) => {
    const isActive = activeResizeCol === col;
    const isHovered = hoveredResizeCol === col;
    return /* @__PURE__ */ jsx(
      "div",
      {
        onMouseDown: (e) => handleResizeStart(e, col),
        onClick: (e) => e.stopPropagation(),
        onMouseEnter: () => setHoveredResizeCol(col),
        onMouseLeave: () => setHoveredResizeCol(null),
        title: "Drag to resize",
        style: {
          position: "absolute",
          right: 0,
          top: 0,
          bottom: 0,
          width: "8px",
          cursor: "col-resize",
          zIndex: 30,
          display: "flex",
          alignItems: "stretch",
          justifyContent: "center"
        },
        children: /* @__PURE__ */ jsx("div", { style: {
          width: "2px",
          backgroundColor: isActive ? "#106ba3" : isHovered ? "#adb5bd" : "#d8dde3",
          transition: "background-color 0.15s",
          borderRadius: "1px"
        } })
      }
    );
  };
  return /* @__PURE__ */ jsxs(
    "div",
    {
      onDragOver: handleDragOver,
      onDragLeave: handleDragLeave,
      onDrop: handleDrop,
      tabIndex: 0,
      onKeyDown: handleKeyDown,
      className: "project-files-table-view",
      style: {
        padding: "20px",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        outline: "none",
        backgroundColor: isDragOver ? "#f0f8ff" : "transparent",
        transition: "background-color 0.2s ease",
        position: "relative"
      },
      children: [
        isUploading && /* @__PURE__ */ jsx("div", { style: {
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(255, 255, 255, 0.7)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1e3,
          borderRadius: "3px"
        }, children: /* @__PURE__ */ jsxs("div", { style: { display: "flex", flexDirection: "column", alignItems: "center" }, children: [
          /* @__PURE__ */ jsx(Spinner, { size: 50, intent: Intent.PRIMARY }),
          /* @__PURE__ */ jsx("h4", { style: { marginTop: 10 }, children: "Uploading..." })
        ] }) }),
        /* @__PURE__ */ jsxs("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }, children: [
          /* @__PURE__ */ jsx("h3", { className: Classes.HEADING, children: urlSearchQuery.trim() ? `Search results for "${urlSearchQuery.trim()}"` : filterQuery.trim() ? `Search results for "${filterQuery.trim()}"` : isRecentFiles ? "Recent Files" : folderPath === "projects" ? "Project Files: /" : `Project Files: /${folderPath}` }),
          /* @__PURE__ */ jsxs("div", { style: { display: "flex", gap: "10px", alignItems: "center" }, children: [
            /* @__PURE__ */ jsx(
              Button,
              {
                minimal: true,
                small: true,
                icon: "wrench",
                title: "Run Build",
                onClick: () => window.dispatchEvent(new CustomEvent("run-assembly-build"))
              }
            ),
            /* @__PURE__ */ jsxs("div", { style: { position: "relative" }, children: [
              /* @__PURE__ */ jsx(
                InputGroup,
                {
                  id: "table-search-input",
                  "data-testid": "search-input",
                  leftIcon: "search",
                  placeholder: "Search...",
                  value: filterQuery,
                  onChange: (e) => setFilterQuery(e.target.value),
                  onKeyDown: handleSearchKeyDown,
                  rightElement: filterQuery ? /* @__PURE__ */ jsx(Button, { minimal: true, icon: "cross", onClick: () => setFilterQuery("") }) : void 0
                }
              ),
              ghostSuggestion && !suggestionDismissed && /* @__PURE__ */ jsxs(
                "div",
                {
                  "data-testid": "search-ghost-suggestion",
                  "aria-hidden": "true",
                  style: {
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    pointerEvents: "none",
                    display: "flex",
                    alignItems: "center",
                    paddingLeft: "30px",
                    paddingRight: "30px",
                    fontSize: "14px",
                    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                    whiteSpace: "pre",
                    overflow: "hidden",
                    color: "transparent",
                    zIndex: 2
                  },
                  children: [
                    /* @__PURE__ */ jsx("span", { children: filterQuery }),
                    /* @__PURE__ */ jsx("span", { style: { color: "#a0a0a0", opacity: 0.85 }, "data-testid": "ghost-suffix", children: ghostSuggestion.suffix })
                  ]
                }
              )
            ] })
          ] })
        ] }),
        /* @__PURE__ */ jsx(
          "div",
          {
            ref: containerRef,
            tabIndex: 0,
            className: "project-files-table-view",
            style: { flexGrow: 1, overflow: "auto", border: "1px solid #ddd", borderRadius: "3px", backgroundColor: "white", outline: "none" },
            onContextMenu: (e) => {
              e.preventDefault();
              showFileContextMenu(e, {
                path: folderPath === "projects" || !folderPath ? "." : folderPath,
                type: "directory"
              }, { source: "table" });
            },
            children: /* @__PURE__ */ jsxs(HTMLTable, { interactive: true, striped: true, style: { width: "fit-content", minWidth: "100%", tableLayout: "fixed", borderCollapse: "separate", borderSpacing: 0 }, children: [
              /* @__PURE__ */ jsx("thead", { children: /* @__PURE__ */ jsxs("tr", { children: [
                /* @__PURE__ */ jsx("th", { style: { width: "40px", textAlign: "center", position: "sticky", top: 0, backgroundColor: "white", zIndex: 20 } }),
                /* @__PURE__ */ jsxs("th", { onClick: () => handleSort("name"), style: getColumnStyle("name"), children: [
                  "Filename ",
                  renderSortIndicator("name"),
                  renderResizeHandle("name")
                ] }),
                /* @__PURE__ */ jsxs("th", { onClick: () => handleSort("description"), style: getColumnStyle("description"), children: [
                  "Description ",
                  renderSortIndicator("description"),
                  renderResizeHandle("description")
                ] }),
                /* @__PURE__ */ jsxs("th", { onClick: () => handleSort("inInventory"), style: getColumnStyle("inInventory"), children: [
                  "In Inventory ",
                  renderSortIndicator("inInventory"),
                  renderResizeHandle("inInventory")
                ] }),
                /* @__PURE__ */ jsxs("th", { onClick: () => handleSort("modifiedDate"), style: getColumnStyle("modifiedDate"), children: [
                  "Modified Date ",
                  renderSortIndicator("modifiedDate"),
                  renderResizeHandle("modifiedDate")
                ] }),
                isRecentFiles && /* @__PURE__ */ jsxs("th", { onClick: () => handleSort("lastAccessed"), style: getColumnStyle("lastAccessed"), children: [
                  "Last Accessed ",
                  renderSortIndicator("lastAccessed"),
                  renderResizeHandle("lastAccessed")
                ] }),
                /* @__PURE__ */ jsxs("th", { onClick: () => handleSort("path"), style: getColumnStyle("path"), children: [
                  "Folder Path ",
                  renderSortIndicator("path"),
                  renderResizeHandle("path")
                ] })
              ] }) }),
              /* @__PURE__ */ jsxs("tbody", { children: [
                filteredAndSortedItems.map((item, index) => {
                  const pathParts = item.path.split("/");
                  pathParts.pop();
                  const parentPath = pathParts.join("/");
                  const baseIcon = getFileIcon(item);
                  const isSelected = selectedPaths.includes(item.path);
                  const isActive = item.path === activePreviewPath;
                  return /* @__PURE__ */ jsxs(
                    "tr",
                    {
                      draggable: "true",
                      onDragStart: (e) => handleDragStart(e, item),
                      onClick: (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        e.currentTarget.closest(".project-files-table-view")?.focus();
                        const isMultiSelect = e.ctrlKey || e.metaKey;
                        const isRangeSelect = e.shiftKey;
                        const path = item.path;
                        if (isRangeSelect && lastClickedPath) {
                          const allVisiblePaths = filteredAndSortedItems.map((i) => i.path);
                          const startIndex = allVisiblePaths.indexOf(lastClickedPath);
                          const endIndex = allVisiblePaths.indexOf(path);
                          if (startIndex !== -1 && endIndex !== -1) {
                            const start = Math.min(startIndex, endIndex);
                            const end = Math.max(startIndex, endIndex);
                            const rangePaths = allVisiblePaths.slice(start, end + 1);
                            setSelectedPaths((prev) => {
                              const newSet = /* @__PURE__ */ new Set([...prev, ...rangePaths]);
                              return Array.from(newSet);
                            });
                            return;
                          }
                        }
                        setLastClickedPath(path);
                        if (isMultiSelect) {
                          setSelectedPaths((prev) => {
                            if (prev.includes(path)) {
                              return prev.filter((p) => p !== path);
                            } else {
                              return [...prev, path];
                            }
                          });
                          return;
                        }
                        setSelectedPaths([path]);
                        if (item.type === "file") {
                          const isPreviewable = ALL_EDITABLE_EXTENSIONS.some((ext) => item.path.toLowerCase().endsWith(ext));
                          if (isPreviewable) {
                            onPreview && onPreview(item.path, item.location);
                          } else {
                            onNavigate(item.path);
                          }
                        } else {
                          onNavigate(item.path);
                        }
                      },
                      onDoubleClick: (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onNavigate(item.path);
                      },
                      onContextMenu: (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        let itemsToUse = [item];
                        if (selectedPaths.includes(item.path)) {
                          itemsToUse = selectedPaths.map((p) => filteredAndSortedItems.find((i) => i.path === p)).filter(Boolean);
                        } else {
                          setSelectedPaths([item.path]);
                          setLastClickedPath(item.path);
                        }
                        showFileContextMenu(e, itemsToUse, { source: "table" });
                      },
                      className: classNames({
                        "active-preview-row": isActive,
                        [Classes.TREE_NODE_SELECTED]: isSelected
                      }),
                      style: {
                        cursor: "grab",
                        userSelect: "none",
                        backgroundColor: isSelected ? "#137cbd" : isActive ? "#e1f0fe" : void 0,
                        color: isSelected ? "white" : void 0
                      },
                      children: [
                        /* @__PURE__ */ jsx("td", { style: { textAlign: "center", width: "40px", overflow: "hidden" }, children: /* @__PURE__ */ jsx(
                          IconWithGitOverlay,
                          {
                            baseIcon,
                            isSymlink: item.isSymlink,
                            gitStatus: item.isNewUnsaved ? null : item.path in (gitStatus || {}) ? gitStatus[item.path] : gitStatus ? "tracked" : null
                          }
                        ) }),
                        /* @__PURE__ */ jsx("td", { style: { width: columnWidths.name, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }, children: renamingPath && renamingSource === "table" && (renamingPath === item.path || renamingPath === `projects/${item.path}`) ? /* @__PURE__ */ jsx(
                          InputGroup,
                          {
                            "data-testid": "rename-input",
                            autoFocus: true,
                            small: true,
                            value: tempRenameValue,
                            onChange: (e) => window.dispatchEvent(new CustomEvent("file-operation-rename-change", { detail: { value: e.target.value } })),
                            onKeyDown: (e) => {
                              if (e.key === "Enter") {
                                e.stopPropagation();
                                window.dispatchEvent(new CustomEvent("file-operation-rename-confirm"));
                              } else if (e.key === "Escape") {
                                e.stopPropagation();
                                window.dispatchEvent(new CustomEvent("file-operation-rename-cancel"));
                              }
                            },
                            onBlur: () => window.dispatchEvent(new CustomEvent("file-operation-rename-cancel")),
                            onFocus: (e) => {
                              const input = e.target;
                              const val = input.value;
                              const ext = ALL_EDITABLE_EXTENSIONS.find((ex) => val.endsWith(ex)) || "";
                              const nameWithoutExt = val.slice(0, val.length - ext.length);
                              setTimeout(() => input.setSelectionRange(0, nameWithoutExt.length), 0);
                            },
                            style: { width: "100%" },
                            onClick: (e) => e.stopPropagation()
                          }
                        ) : /* @__PURE__ */ jsx(
                          "span",
                          {
                            onClick: (e) => {
                              if (isSelected && selectedPaths.length === 1 && lastClickedPath === item.path) {
                                e.stopPropagation();
                                setRenamingState(item.path, item.name);
                              }
                            },
                            children: item.name
                          }
                        ) }),
                        /* @__PURE__ */ jsx("td", { style: { width: columnWidths.description, maxWidth: 0, overflow: "hidden" }, children: item.description ? /* @__PURE__ */ jsx(Tooltip, { content: item.description, hoverOpenDelay: 1e3, usePortal: true, position: "top", targetTagName: "div", wrapperTagName: "div", style: { width: "100%", display: "block" }, children: /* @__PURE__ */ jsx("div", { className: "ellipsis-text", style: { width: "100%" }, children: item.description }) }) : "" }),
                        /* @__PURE__ */ jsx("td", { style: { width: columnWidths.inInventory, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }, children: (() => {
                          const isInInv = item.inInventory !== void 0 ? Boolean(item.inInventory) : Boolean(item.in_inventory);
                          return isInInv ? /* @__PURE__ */ jsx("span", { style: { display: "inline-flex", alignItems: "center", color: isSelected ? "inherit" : "#107310" }, children: /* @__PURE__ */ jsx(Icon, { icon: "tick", intent: isSelected ? "none" : "success", iconSize: 14 }) }) : null;
                        })() }),
                        /* @__PURE__ */ jsx("td", { style: { width: columnWidths.modifiedDate, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }, children: formatDate(item.modifiedDate) }),
                        isRecentFiles && /* @__PURE__ */ jsx("td", { style: { width: columnWidths.lastAccessed, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }, children: formatDate(item.lastAccessed) }),
                        /* @__PURE__ */ jsx("td", { style: { width: columnWidths.path, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }, children: parentPath || "/" })
                      ]
                    },
                    `${item.path}-${item.description || index}`
                  );
                }),
                filteredAndSortedItems.length === 0 && /* @__PURE__ */ jsx("tr", { children: /* @__PURE__ */ jsx("td", { colSpan: isRecentFiles ? 7 : 6, style: { textAlign: "center", color: "#888", padding: "20px" }, children: "No items found." }) })
              ] })
            ] })
          }
        )
      ]
    }
  );
});
export default ProjectFilesTableView;
